/**
 * Рамка района на карте (agroai-0yt.2): маска «мир с дыркой района» и точка
 * подписи. Чистые функции — без maplibre, проверяются тестом.
 *
 * Маска затемняет только то, что ВНЕ района: внутри снимок остаётся без вуали,
 * и фигура (район) отделяется от фона (соседи), а не тонет вместе с ним.
 */
import type { Feature, FeatureCollection, Polygon, Position } from "geojson";
import { defineMessages } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  district: {
    id: "datamap.district.label",
    defaultMessage: "{name} район",
    description:
      "Подпись района на карте, когда в названии нет слова «район»: {name} — название из данных («Бука» → «Бука район»); подпись короткая, над контурами",
  },
});

type Ring = Position[];

interface GeometryLike {
  type?: unknown;
  coordinates?: unknown;
}

/** Внешние кольца всех полигонов района; дырки самого района не нужны. */
export function outerRings(features: ReadonlyArray<{ geometry: unknown }>): Ring[] {
  const rings: Ring[] = [];
  for (const feature of features) {
    const g = feature.geometry as GeometryLike | null;
    if (!g) continue;
    if (g.type === "Polygon") {
      const outer = (g.coordinates as Ring[] | undefined)?.[0];
      if (outer?.length) rings.push(outer);
    } else if (g.type === "MultiPolygon") {
      for (const polygon of (g.coordinates as Ring[][] | undefined) ?? []) {
        if (polygon[0]?.length) rings.push(polygon[0]);
      }
    }
  }
  return rings;
}

const WORLD: Ring = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

/** Полигон «весь мир минус район». Нет геометрии района — нет и маски. */
export function outsideMask(features: ReadonlyArray<{ geometry: unknown }>): FeatureCollection {
  const holes = outerRings(features);
  if (!holes.length) return { type: "FeatureCollection", features: [] };
  const mask: Feature<Polygon> = {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [WORLD, ...holes] },
  };
  return { type: "FeatureCollection", features: [mask] };
}

/**
 * Точка подписи — центр рамки самого большого кольца (по числу вершин — для
 * района этого достаточно: подпись не обязана стоять в геометрическом
 * центре масс, она обязана стоять над районом).
 */
export function labelPoint(features: ReadonlyArray<{ geometry: unknown }>): [number, number] | null {
  const rings = outerRings(features);
  if (!rings.length) return null;
  const ring = rings.reduce((a, b) => (b.length > a.length ? b : a));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring as [number, number][]) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

/** «Бука» → «Букинский район» не угадываем: имя канона + «район», если его нет. */
export function districtLabel(name: string | null | undefined): string | null {
  const text = name?.trim();
  if (!text) return null;
  return /район|tuman/i.test(text) ? text : tr(m.district, { name: text });
}
